import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, gr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Users, Boat, ShieldStar, Storefront, SignOut, Trash } from "@phosphor-icons/react";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <Card className="flex items-center gap-4 rounded-sm border-border p-5" data-testid={testid}>
    <span className="inline-flex rounded-sm bg-[#234E7D] p-3 text-white"><Icon size={22} weight="bold" /></span>
    <div>
      <div className="text-2xl font-extrabold tabular-nums text-[#234E7D]">{value}</div>
      <div className="text-xs font-semibold tracking-wider text-muted-foreground">{label}</div>
    </div>
  </Card>
);

export default function AdminPanel({ onOpenMarina }) {
  const { user, logout } = useAuth();
  const qc = useQueryClient();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
  });

  const remove = async (email) => {
    try {
      await api.delete(`/admin/users/${encodeURIComponent(email)}`);
      await qc.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Ο χρήστης διαγράφηκε");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία διαγραφής");
    }
  };

  const totalBoats = users.reduce((s, u) => s + (u.boat_count || 0), 0);
  const totalMarinas = users.filter((u) => u.marina_name).length;

  return (
    <div className="min-h-screen bg-secondary/40">
      <header className="flex items-center justify-between border-b border-border bg-[#234E7D] px-8 py-4 text-white">
        <div className="flex items-center gap-3">
          <ShieldStar size={26} weight="bold" />
          <div>
            <div className="font-head text-lg font-extrabold leading-none">Πίνακας διαχείρισης</div>
            <div className="text-xs text-white/70">NAVIPARK · {user?.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" className="rounded-sm" onClick={onOpenMarina} data-testid="open-marina-btn">
            <Storefront size={18} weight="bold" /> Η μαρίνα μου
          </Button>
          <Button variant="ghost" className="rounded-sm text-white hover:bg-white/10 hover:text-white" onClick={logout} data-testid="admin-logout-btn">
            <SignOut size={18} weight="bold" /> Αποσύνδεση
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-8 py-8">
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Stat icon={Users} label="Χρήστες" value={users.length} testid="stat-users" />
          <Stat icon={Storefront} label="Μαρίνες" value={totalMarinas} testid="stat-marinas" />
          <Stat icon={Boat} label="Σκάφη σύνολο" value={totalBoats} testid="stat-boats" />
        </div>

        <Card className="overflow-hidden rounded-sm border-border">
          <div className="border-b border-border bg-white px-5 py-3 font-head font-extrabold text-[#234E7D]">Εγγεγραμμένοι χρήστες</div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="admin-users-table">
              <thead className="bg-secondary/60 text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-semibold">Χρήστης</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Μαρίνα</th>
                  <th className="px-4 py-3 font-semibold">Σκάφη</th>
                  <th className="px-4 py-3 font-semibold">Αναμονή</th>
                  <th className="px-4 py-3 font-semibold">Εγγραφή</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {isLoading ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Φόρτωση...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Δεν υπάρχουν χρήστες.</td></tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.email} className="hover:bg-secondary/40" data-testid={`admin-user-${u.email}`}>
                      <td className="px-4 py-3 font-medium">
                        {u.name}
                        {u.platform_role === "superadmin" && (
                          <span className="ml-2 inline-flex items-center gap-1 rounded-sm bg-[#234E7D] px-1.5 py-0.5 text-[10px] font-semibold text-white"><ShieldStar size={10} weight="bold" /> admin</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                      <td className="px-4 py-3">{u.marina_name || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-4 py-3 tabular-nums">{u.boat_count}</td>
                      <td className="px-4 py-3 tabular-nums">{u.waitlist_count}</td>
                      <td className="px-4 py-3 tabular-nums text-muted-foreground">{u.created_at ? gr(u.created_at.slice(0, 10)) : "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {u.platform_role !== "superadmin" && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="rounded-sm text-destructive hover:bg-destructive/10" data-testid={`delete-user-${u.email}`}>
                                <Trash size={16} weight="bold" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="rounded-sm">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Διαγραφή χρήστη;</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Θα διαγραφεί ο χρήστης «{u.name}» ({u.email}) μαζί με τη μαρίνα, τα σκάφη και τη λίστα αναμονής του. Η ενέργεια δεν αναιρείται.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="rounded-sm">Άκυρο</AlertDialogCancel>
                                <AlertDialogAction className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={() => remove(u.email)} data-testid={`confirm-delete-user-${u.email}`}>
                                  Διαγραφή
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </main>
    </div>
  );
}
